import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/Badge'
import { Loading02Icon } from '@untitledui-icons/react/line'
import type { SimRunStatus } from '@/types/simulation'

interface StatusBadgeProps {
  status: SimRunStatus
}

const statusVariants: Record<SimRunStatus, 'gray' | 'brand' | 'success' | 'error' | 'warning'> = {
  pending: 'gray',
  running: 'brand',
  completed: 'success',
  failed: 'error',
}

/**
 * Badge component for displaying simulation run status
 */
export function StatusBadge({ status }: StatusBadgeProps) {
  const { t } = useTranslation()

  return (
    <Badge variant={statusVariants[status]} size="sm">
      <span className="flex items-center gap-1">
        {status === 'running' && (
          <Loading02Icon className="h-3 w-3 animate-spin" />
        )}
        {t(`pages.simulations.statuses.${status}`)}
      </span>
    </Badge>
  )
}

interface PassFailBadgeProps {
  passed: boolean | null
  label?: string
}

/**
 * Badge component for displaying pass/fail status
 */
export function PassFailBadge({ passed, label }: PassFailBadgeProps) {
  const { t } = useTranslation()

  if (passed === null) {
    return (
      <Badge variant="gray" size="sm">
        {label ?? t('pages.simulations.not_run')}
      </Badge>
    )
  }

  return (
    <Badge variant={passed ? 'success' : 'error'} size="sm">
      {label ?? (passed ? t('pages.simulations.passed') : t('pages.simulations.failed'))}
    </Badge>
  )
}

export default StatusBadge
